import { createFileRoute } from "@tanstack/react-router";
import { marketingHead } from "@/lib/marketing/seo";
import { breadcrumbs, webPageOf } from "@/lib/marketing/ppl-schema";
import { CaseStudyGate, useCaseStudyAccess } from "@/components/marketing/proof/case-study-gate";
import { MarketingShell } from "@/components/marketing/site/marketing-shell";
import { MarketingCta } from "@/components/marketing/site/marketing-cta";
import { StudyHero } from "@/components/marketing/proof/case-study";
import { StudyBody } from "@/components/marketing/proof/study-body";
import { StudyFooter } from "@/components/marketing/proof/study-footer";
import { NancyStudySummary } from "@/routes/v2/-components/nancy-study-summary";
import { NancyStudyBackground } from "@/routes/v2/-components/nancy-study-background";
import { NancyStudyDone } from "@/routes/v2/-components/nancy-study-done";
import { NancyStudyMethod } from "@/routes/v2/-components/nancy-study-method";
import { NancyStudyResults } from "@/routes/v2/-components/nancy-study-results";
import { NancyStudyConclusions } from "@/routes/v2/-components/nancy-study-conclusions";
import { NancyStudyAppendix } from "@/routes/v2/-components/nancy-study-appendix";
import { NancyStudyReferences } from "@/routes/v2/-components/nancy-study-references";

/* Nancy case study v2.0 (8 September 2026). Per the operator, Nancy did the purchasing at both
 * communities from June to August 2026 under the purchasing lead's oversight; the cost figures
 * measure that period. */

function MarketingCaseStudyNancy() {
  /* The report is the site's lead magnet: everything a reader needs to judge
     the numbers is above the gate, and the working below it. */
  const { unlocked, unlock } = useCaseStudyAccess();

  return (
    <MarketingShell>
      <StudyHero
        eyebrow="People AI, Case study, September 2026"
        title="Nancy, menu-driven food purchasing for two community kitchens"
        standfirst="Menu-driven purchasing at Pine Lodge and Reunion Inn: three months with Nancy doing the buying under the purchasing lead's oversight, and about 30% off the food bill, half from buying to the menu, half from price matching."
        testId="text-case-study-nancy-title"
      />

      <StudyBody>
        <NancyStudySummary />

        {unlocked ? (
          <>
            <NancyStudyBackground />
            <NancyStudyDone />
            <NancyStudyMethod />
            <NancyStudyResults />
            <NancyStudyConclusions />
            <NancyStudyAppendix />
            <NancyStudyReferences />
          </>
        ) : (
          <CaseStudyGate
            study="Nancy, menu-driven food purchasing for two community kitchens"
            slug="nancy-purchasing"
            promise="Three months of purchasing measured from the vendors' own records: the method, the allocation sensitivity, where the saving comes from, how Nancy works and what she records on every run, and the monthly appendix."
            onUnlocked={unlock}
            className="my-12"
          />
        )}

        <StudyFooter>
          Prepared 8 September 2026. Figures are as read on that date. The purchasing lead is
          referred to by role.
        </StudyFooter>
      </StudyBody>

      <MarketingCta
        title="See what your kitchens spend"
        subtitle="Book a demo, or read how the same communities' phones are answered."
        secondaryLabel="Read the Sarah study"
        secondaryHref="/case-studies/sarah-front-desk"
      />
    </MarketingShell>
  );
}

export const Route = createFileRoute("/v2/case-studies/nancy-purchasing")({
  component: MarketingCaseStudyNancy,
  head: () => {
    const seo = {
      title:
        "Case study: Nancy, menu-driven food purchasing for two community kitchens | PEOPLE AI Workforce",
      description:
        "Three months of menu-driven purchasing at two assisted-living kitchens with Nancy doing the buying under the purchasing lead's oversight: the measured cost per resident-day, how it was measured, and how Nancy works.",
      path: "/v2/case-studies/nancy-purchasing",
    };
    return marketingHead({
      ...seo,
      schema: [
        webPageOf(seo),
        breadcrumbs(
          { name: "Case studies", path: "/case-studies" },
          { name: "Nancy, purchasing", path: "/case-studies/nancy-purchasing" },
        ),
      ],
    });
  },
});
